import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import './StravaWebhookAdminPage.css';

const createStravaWebhook = httpsCallable(functions, 'createStravaWebhookSubscription');
const viewStravaWebhook = httpsCallable(functions, 'viewStravaWebhookSubscription');
const deleteStravaWebhook = httpsCallable(functions, 'deleteStravaWebhookSubscription');

function StravaWebhookAdminPage({ user }) {
    const [subscription, setSubscription] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');

    const [callbackUrl, setCallbackUrl] = useState('');
    const [verifyToken, setVerifyToken] = useState('');

    // Construct default callback URL - replace with your actual function region and project ID if needed
    const defaultCallback = `https://mythicmaps.ai/stravaWebhook`;

    useEffect(() => {
        setCallbackUrl(defaultCallback);
        fetchSubscriptionDetails();
    }, []);

    const fetchSubscriptionDetails = async () => {
        setIsLoading(true);
        setError(null);
        setMessage('');
        try {
            const result = await viewStravaWebhook();
            if (result.data.success) {
                setSubscription(result.data.data); // data.data contains the subscription object or null
                if (!result.data.data) {
                    setMessage("No active Strava webhook subscription found.");
                }
            } else {
                setError(result.data.message || "Failed to fetch subscription details.");
            }
        } catch (err) {
            console.error("Error fetching subscription:", err);
            setError(err.message || 'An unknown error occurred.');
        }
        setIsLoading(false);
    };

    const handleCreateSubscription = async (e) => {
        e.preventDefault();
        if (!callbackUrl || !verifyToken) {
            setError("Callback URL and Verify Token are required.");
            return;
        }
        setIsLoading(true);
        setError(null);
        setMessage('');
        try {
            console.log("Sending to createStravaWebhook:", { callbackUrl: callbackUrl, verifyToken: verifyToken });
            const result = await createStravaWebhook({ callbackUrl: callbackUrl, verifyToken: verifyToken });
            if (result.data.success) {
                setMessage("Subscription creation requested. Strava will send a validation request to your callback URL. Check logs.");
                // Strava doesn't return the full subscription object immediately on create, 
                // it returns an object like { id: <sub_id> }. 
                // We should re-fetch to get full details after a moment.
                setTimeout(fetchSubscriptionDetails, 5000); // Re-fetch after 5s
            } else {
                setError(result.data.message || "Failed to create subscription.");
            }
        } catch (err) {
            console.error("Error creating subscription:", err);
            setError(err.message || 'An unknown error occurred during creation.');
        }
        setIsLoading(false);
    };

    const handleDeleteSubscription = async () => {
        if (!subscription || !subscription.id) {
            setError("No subscription ID found to delete.");
            return;
        }
        setIsLoading(true);
        setError(null);
        setMessage('');
        try {
            const result = await deleteStravaWebhook({ subscriptionId: subscription.id });
            if (result.data.success) {
                setMessage("Subscription deleted successfully.");
                setSubscription(null);
            } else {
                setError(result.data.message || "Failed to delete subscription.");
            }
        } catch (err) {
            console.error("Error deleting subscription:", err);
            setError(err.message || 'An unknown error occurred during deletion.');
        }
        setIsLoading(false);
    };

    // Basic admin check (can be enhanced with custom claims for roles)
    // For now, let's assume if a user prop is passed and is not null, they are an admin for this page.
    // In a real app, you'd check a custom claim like user.customClaims.admin === true
    if (!user) { // Simplified check, replace with proper admin role check
        return <div className="container"><h2>Admin Access Required</h2><p>You must be an administrator to manage Strava Webhooks.</p></div>;
    }

    return (
        <div className="container strava-webhook-admin-page">
            <h1>Strava Webhook Management</h1>

            {message && <p className={`message ${error ? 'error-msg' : 'success-msg'}`}>{message}</p>}
            {error && <p className="message error-msg">Error: {error}</p>}

            <div className="subscription-status-section">
                <h2>Current Subscription Status</h2>
                {isLoading && <p>Loading subscription details...</p>}
                {!isLoading && subscription && (
                    <div className="subscription-details">
                        <p><strong>Subscription ID:</strong> {subscription.id}</p>
                        <p><strong>Callback URL:</strong> {subscription.callback_url}</p>
                        <p><strong>Created At:</strong> {new Date(subscription.created_at).toLocaleString()}</p>
                        <p><strong>Updated At:</strong> {new Date(subscription.updated_at).toLocaleString()}</p>
                        <button onClick={handleDeleteSubscription} disabled={isLoading} className="delete-button">
                            Delete Subscription
                        </button>
                    </div>
                )}
                {!isLoading && !subscription && !error && (
                    <p>No active subscription. Use the form below to create one.</p>
                )}
            </div>

            <div className="manage-subscription-section">
                <h2>{subscription ? 'Update (Re-create)' : 'Create'} Subscription</h2>
                <p className="info-text">
                    To update, delete the current subscription and create a new one.
                    Ensure your <code>stravaWebhook</code> Cloud Function is deployed and the Callback URL is correct.
                    The Verify Token must match the <code>strava.webhook_verify_token</code> in your Firebase environment config.
                </p>
                <form onSubmit={handleCreateSubscription} className="webhook-form">
                    <div>
                        <label htmlFor="callbackUrl">Callback URL:</label>
                        <input
                            type="text"
                            id="callbackUrl"
                            value={callbackUrl}
                            onChange={(e) => setCallbackUrl(e.target.value)}
                            required
                            placeholder={defaultCallback}
                        />
                        <p className="input-hint">This should be the URL of your deployed 'stravaWebhook' Firebase Function.</p>
                    </div>
                    <div>
                        <label htmlFor="verifyToken">Verify Token:</label>
                        <input
                            type="text"
                            id="verifyToken"
                            value={verifyToken}
                            onChange={(e) => setVerifyToken(e.target.value)}
                            required
                        />
                        <p className="input-hint">Must match <code>functions.config().strava.webhook_verify_token</code>.</p>
                    </div>
                    <button type="submit" disabled={isLoading} className="action-button">
                        {isLoading ? 'Processing...' : (subscription ? 'Re-create Subscription' : 'Create Subscription')}
                    </button>
                </form>
            </div>
            <div className="verification-check-section">
                <h2>Verify Callback</h2>
                <p>After creating a subscription, Strava sends a GET request to your callback URL. Check your Firebase Function logs for "Strava webhook validation successful".</p>
                <p>You can also manually test your callback URL (ensure it's deployed):</p>
                <code>
                    curl -X GET '{callbackUrl}?hub.mode=subscribe&hub.verify_token=YOUR_VERIFY_TOKEN&hub.challenge=testChallenge123'
                </code>
                <p>Replace YOUR_VERIFY_TOKEN with your actual token. It should respond with: <code>{'{ "hub.challenge": "testChallenge123" }'}</code></p>
            </div>
        </div>
    );
}

export default StravaWebhookAdminPage; 